import { useEffect, useState } from "react"
import {
  type CartItemSchema,
  type CartSchema,
  CartService,
  type CheckoutForm,
  type Item,
  ItemsService,
} from "../client"

import {
  Button,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Tr,
} from "@chakra-ui/react"

import { useNavigate } from "@tanstack/react-router"
import { Formik } from "formik"
import * as Yup from "yup"
import PCInput from "./PCInput"

const OrderInfoFormSchema = Yup.object().shape({
  full_name: Yup.string().required(),
  company_name: Yup.string().required(),
  title: Yup.string().required(),
  street1: Yup.string().required(),
  street2: Yup.string(),
  city: Yup.string().required(),
  state: Yup.string().required(),
  zip_code: Yup.string().required(),
  email: Yup.string().email().required(),
  phone: Yup.string().required(),
})

const OrderInfo: React.FC = () => {
  const navigate = useNavigate()
  const [cart, setCart] = useState<CartSchema | null>(null)
  const [itemNames, setItemNames] = useState<{ [key: string]: string }>({})

  useEffect(() => {
    const fetchCart = async () => {
      try {
        const cartData = await CartService.getCart() // Assuming getCart fetches the cart data
        setCart(cartData)

        // Fetch item names
        const names: { [key: string]: string } = {}
        for (const item of cartData.items) {
          const itemData: Item = await ItemsService.readItem({
            id: item.item_id,
          })
          names[item.item_id] = itemData.name
        }
        setItemNames(names)
      } catch (error) {
        console.error("Failed to fetch cart data:", error)
      }
    }

    fetchCart()
  }, [])

  if (!cart || cart.items.length === 0) {
    return <div>Your cart is empty.</div>
  }

  const totalPrice = cart.items.reduce(
    (sum, item) => sum + Number(item.price),
    0,
  )
  return (
    <TableContainer>
      <Table>
        <Tbody>
          <>
            <tr>
              <Td colSpan={2}>
                <h1>Your Order</h1>
              </Td>
            </tr>
            {cart.items.map((item: CartItemSchema) => (
              <Tr key={item.id}>
                <Td>{itemNames[item.item_id]}</Td>
                <Td valign="top">{item.price}</Td>
              </Tr>
            ))}
            <Tr>
              <Td>
                <strong>Total</strong>
              </Td>
              <Td valign="top">
                <strong>{totalPrice.toFixed(2)}</strong>
              </Td>
            </Tr>

            <Tr>
              <Td colSpan={2}>
                <Formik
                  initialValues={{
                    full_name: "",
                    company_name: "",
                    title: "",
                    street1: "",
                    street2: "",
                    city: "",
                    state: "",
                    zip_code: "",
                    email: "",
                    phone: "",
                  }}
                  validationSchema={OrderInfoFormSchema}
                  onSubmit={async (values) => {
                    try {
                      const order: CheckoutForm = {
                        full_name: values.full_name,
                        company_name: values.company_name,
                        title: values.title,
                        street1: values.street1,
                        street2: values.street2,
                        city: values.city,
                        state: values.state,
                        zip_code: values.zip_code,
                        email: values.email,
                        phone: values.phone,
                      }
                      const updatedOrder = await CartService.orderInfo({
                        requestBody: order,
                      })
                      window.parent.postMessage(
                        {
                          navigateTo: `signature?orderid=${updatedOrder.id}`,
                        },
                        "*",
                      )
                      navigate({
                        to: "/signature",
                        search: { orderid: updatedOrder.id },
                      })
                    } catch (error) {
                      console.error("Error creating order:", error)
                    }
                  }}
                >
                  {({ handleSubmit }) => (
                    <form onSubmit={handleSubmit}>
                      <Stack>
                        <h1>Order Information</h1>
                        <PCInput
                          label="Full Name"
                          name="full_name"
                          type="text"
                        />
                        <PCInput
                          label="Company Name"
                          name="company_name"
                          type="text"
                        />
                        <PCInput label="Title" name="title" type="
                        text" />
                        <PCInput label="Street 1" name="street1" type="text" />
                        <PCInput label="Street 2" name="street2" type="text" />
                        <PCInput label="City" name="city" type="text" />
                        <PCInput label="State" name="state" type="text" />
                        <PCInput label="Zip Code" name="zip_code" type="text" />
                        <PCInput label="Email" name="email" type="email" />
                        <PCInput label="Phone" name="phone" type="text" />
                        <Button type="submit" variant="emphasis">
                          Continue
                        </Button>
                      </Stack>
                    </form>
                  )}
                </Formik>
              </Td>
            </Tr>
          </>
        </Tbody>
      </Table>
    </TableContainer>
  )
}

export default OrderInfo
